/**
 * V1-V2 매핑 상수 정의
 *
 * V1에서 지원하는 네트워크/토큰을 필터링하기 위한 매핑 데이터입니다.
 * workspace-dashboard의 v1-v2-mappings를 기반으로 작성되었습니다.
 *
 * @see /docs/musicgi-v2/filter/V1_SUPPORTED_CURRENCIES.md
 */

/**
 * V1 지원 Currency ID 목록
 *
 * COIN_ID_TO_CURRENCY_ID_MAP의 값들을 Set으로 정의
 * NFT 네트워크 필터링에도 사용
 */
export const V1_SUPPORTED_CURRENCY_IDS = new Set([
  // ==========================================
  // Mainnet
  // ==========================================
  'ETHEREUM', // ID: 1
  'KLAYTN', // ID: 2
  'SOLANA', // ID: 3
  'CHAN:128', // ID: 4 - HECO Mainnet
  'ETH-CLASSIC', // ID: 11
  'LUNIVERSE', // ID: 12
  'XINFIN', // ID: 13 - XDC Network
  'BSC', // ID: 15
  'POLYGON', // ID: 17
  'CHAN:100', // ID: 19 - Gnosis Chain
  'CHAN:250', // ID: 20 - Fantom
  'CHAN:42220', // ID: 21 - Celo
  'CHAN:11', // ID: 22 - Metadium
  'CHAN:321', // ID: 23 - KCC
  'CHAN:19', // ID: 24 - Songbird
  'CHAN:43114', // ID: 25 - Avalanche C-Chain
  'CHAN:246', // ID: 26 - Energy Web Chain
  'CHAN:42161', // ID: 27 - Arbitrum
  'CHAN:288', // ID: 28 - Boba L2
  'CHAN:66', // ID: 29 - OKT Chain
  'CHAN:1666600000', // ID: 30 - Harmony
  'CHAN:2017', // ID: 31 - Orbit Chain
  'CHAN:25', // ID: 33 - Cronos
  'CHAN:X-PALM', // ID: 34 - Palm
  'CHAN:14', // ID: 35 - Flare Network
  'CHAN:248', // ID: 36 - Oasys Games
  'NEAR', // ID: 42
  'near_pt', // ID: 43 - NEAR Protocol
  'CHAN:255', // ID: 46 - Kroma
  'CHAN:4613', // ID: 49 - VERY Mainnet
  'CHAN:8453', // ID: 50 - Base
  'CHAN:10', // ID: 54 - Optimism
  'CHAN:37', // ID: 56 - XPLA
  'CHAN:7300', // ID: 57 - XPLAVerse
  'CHAN:204', // ID: 63 - opBNB
  'CHAN:81', // ID: 65 - Japan Open Chain
  'RIPPLE', // ID: 70 - XRPL
  'CHAN:1689', // ID: 71 - Nero Chain
  'CHAN:41923', // ID: 74 - EduChain

  // ==========================================
  // Testnet
  // ==========================================
  'FLR-COSTON', // ID: 9 - Flare Coston Testnet
  'KLAYTN-TESTNET', // ID: 10
  'XDC-APOTHEM', // ID: 14 - XDC Apothem Testnet
  'BSC-TESTNET', // ID: 16
  'EVMT:12', // ID: 38 - Metadium Testnet
  'EVMT:1666700000', // ID: 40 - Harmony Testnet
  'EVMT:2731', // ID: 44 - TIME Testnet
  'near_pt_testnet', // ID: 45 - NEAR Protocol Testnet
  'EVMT:2358', // ID: 47 - Kroma Sepolia
  'EVMT:11155111', // ID: 48 - Ethereum Sepolia
  'EVMT:84532', // ID: 51 - Base Sepolia
  'EVMT:80002', // ID: 52 - Polygon Amoy
  'EVMT:9372', // ID: 53 - Oasys Games Testnet
  'EVMT:2225', // ID: 55 - XPLAVerse Testnet
  'SOLANA-DEVNET', // ID: 58
  'evmXPLA-Testnet', // ID: 59 - XPLA Testnet
  'EVMT:42069', // ID: 60 - DSRV Payments Demonet
  'EVMT:5611', // ID: 64 - opBNB Testnet
  'EVMT:10081', // ID: 66 - Japan Open Chain Testnet
  'EVMT:656476', // ID: 67 - OpenCampus Testnet
  'XRP-TESTNET', // ID: 72
  'NEAR-TESTNET', // ID: 73
  'EVMT:20250616', // ID: 75 - LK Testnet
])

/**
 * V1 지원 토큰 타입 목록
 *
 * TOKEN_TYPE_DEVICE_STORE_ID_MAP의 값들을 Set으로 정의
 */
export const V1_SUPPORTED_TOKEN_TYPES = new Set([
  // ==========================================
  // Mainnet Token Types
  // ==========================================
  'ERC20', // ID: 1 - Ethereum
  'KLAYTN-ERC20', // ID: 2 - Klaytn
  'SPL-TOKEN', // ID: 3 - Solana
  'CH20:128', // ID: 4 - HECO
  'LUNIVERSE-TKN', // ID: 12 - Luniverse
  'XRC20', // ID: 13 - XDC
  'BEP20', // ID: 15 - BSC
  'POLYGON-ERC20', // ID: 17 - Polygon
  'CH20:100', // ID: 19 - Gnosis
  'CH20:250', // ID: 20 - Fantom
  'CH20:42220', // ID: 21 - Celo
  'CH20:11', // ID: 22 - Metadium
  'CH20:321', // ID: 23 - KCC
  'CH20:19', // ID: 24 - Songbird
  'CH20:43114', // ID: 25 - Avalanche
  'CH20:246', // ID: 26 - Energy Web
  'CH20:42161', // ID: 27 - Arbitrum
  'CH20:288', // ID: 28 - Boba
  'CH20:66', // ID: 29 - OKT
  'CH20:1666600000', // ID: 30 - Harmony
  'CH20:2017', // ID: 31 - Orbit
  'CH20:25', // ID: 33 - Cronos
  'CH20:14', // ID: 35 - Flare
  'CH20:248', // ID: 36 - Oasys
  'NEAR-TOKEN', // ID: 42 - NEAR
  'near_pt_token', // ID: 43 - NEAR PT
  'CH20:255', // ID: 46 - Kroma
  'CH20:8453', // ID: 50 - Base
  'CH20:10', // ID: 54 - Optimism
  'CH20:37', // ID: 56 - XPLA
  'CH20:204', // ID: 63 - opBNB
  'CH20:81', // ID: 65 - Japan Open Chain
  'RIPPLE-TA', // ID: 70 - XRP
  'CH20:41923', // ID: 74 - EduChain

  // ==========================================
  // Testnet Token Types
  // ==========================================
  'FLR20-COSTON', // ID: 9 - Flare Coston
  'KRC20-TESTNET', // ID: 10 - Klaytn Testnet
  'BEP20-TESTNET', // ID: 16 - BSC Testnet
  'E20T:1666700000', // ID: 40 - Harmony Testnet
  'near_pt_testnet_token', // ID: 45 - NEAR PT Testnet
  'E20T:2358', // ID: 47 - Kroma Sepolia
  'E20T:11155111', // ID: 48 - Ethereum Sepolia
  'E20T:84532', // ID: 51 - Base Sepolia
  'E20T:9372', // ID: 53 - Oasys Testnet
  'E20T:2225', // ID: 55 - XPLAVerse Testnet
  'SPL-DEVNET', // ID: 58 - Solana Devnet
  'E20T:42069', // ID: 60 - DSRV Demonet
  'E20T:10081', // ID: 66 - JOC Testnet
  'E20T:656476', // ID: 67 - OpenCampus Testnet
  'NEAR-TESTNET-TOKEN', // ID: 73 - NEAR Testnet
])

/**
 * V1 Coin ID → V2 Currency ID 매핑 (참조용)
 *
 * 역변환이 필요한 경우 사용
 */
export const COIN_ID_TO_CURRENCY_ID_MAP: Record<number, string> = {
  1: 'ETHEREUM',
  2: 'KLAYTN',
  3: 'SOLANA',
  4: 'CHAN:128',
  9: 'FLR-COSTON',
  10: 'KLAYTN-TESTNET',
  11: 'ETH-CLASSIC',
  12: 'LUNIVERSE',
  13: 'XINFIN',
  14: 'XDC-APOTHEM',
  15: 'BSC',
  16: 'BSC-TESTNET',
  17: 'POLYGON',
  19: 'CHAN:100',
  20: 'CHAN:250',
  21: 'CHAN:42220',
  22: 'CHAN:11',
  23: 'CHAN:321',
  24: 'CHAN:19',
  25: 'CHAN:43114',
  26: 'CHAN:246',
  27: 'CHAN:42161',
  28: 'CHAN:288',
  29: 'CHAN:66',
  30: 'CHAN:1666600000',
  31: 'CHAN:2017',
  33: 'CHAN:25',
  34: 'CHAN:X-PALM',
  35: 'CHAN:14',
  36: 'CHAN:248',
  38: 'EVMT:12',
  40: 'EVMT:1666700000',
  42: 'NEAR',
  43: 'near_pt',
  44: 'EVMT:2731',
  45: 'near_pt_testnet',
  46: 'CHAN:255',
  47: 'EVMT:2358',
  48: 'EVMT:11155111',
  49: 'CHAN:4613',
  50: 'CHAN:8453',
  51: 'EVMT:84532',
  52: 'EVMT:80002',
  53: 'EVMT:9372',
  54: 'CHAN:10',
  55: 'EVMT:2225',
  56: 'CHAN:37',
  57: 'CHAN:7300',
  58: 'SOLANA-DEVNET',
  59: 'evmXPLA-Testnet',
  60: 'EVMT:42069',
  63: 'CHAN:204',
  64: 'EVMT:5611',
  65: 'CHAN:81',
  66: 'EVMT:10081',
  67: 'EVMT:656476',
  70: 'RIPPLE',
  71: 'CHAN:1689',
  72: 'XRP-TESTNET',
  73: 'NEAR-TESTNET',
  74: 'CHAN:41923',
  75: 'EVMT:20250616',
}

/**
 * V2 Currency ID → V1 Coin ID 역매핑 (자동 생성)
 */
export const CURRENCY_ID_TO_COIN_ID_MAP: Record<string, number> =
  Object.entries(COIN_ID_TO_CURRENCY_ID_MAP).reduce(
    (acc, [coinId, currencyId]) => {
      if (!acc[currencyId]) {
        acc[currencyId] = parseInt(coinId, 10)
      }
      return acc
    },
    {} as Record<string, number>,
  )

/**
 * 계정 추가에서 숨길 네트워크 목록
 *
 * 이미 생성된 계정은 표시되지만, 새 계정 추가 UI에서는 숨김
 * near_pt, near_pt_testnet: NEAR Protocol PT 버전 (별도 관리)
 */
export const HIDDEN_FROM_ADD_ACCOUNT_CURRENCY_IDS = new Set([
  'near_pt', // NEAR Protocol (PT)
  'near_pt_testnet', // NEAR Protocol Testnet (PT)
])

/**
 * 계정 추가에서 숨길 토큰 타입 목록
 */
export const HIDDEN_FROM_ADD_ACCOUNT_TOKEN_TYPES = new Set([
  'near_pt_token', // NEAR PT Token
  'near_pt_testnet_token', // NEAR PT Testnet Token
])
